import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatAuDateShort } from "@/lib/date";
import { toast } from "sonner";

/**
 * UnallocatedView renders the pool of volunteers who asked the organiser to
 * pick a role for them. It's used in two places:
 *   • `variant="sidebar"` — the sticky left-hand vertical stack next to the
 *     spreadsheet (feedback #11). Compact cards, sort dropdown, drag targets.
 *   • `variant="panel"`   — the legacy full-width tab view, kept as a fallback.
 */
export function UnallocatedView({ items, days, eventId, onAssigned, onDropHere, variant = "panel", collapsed = false, onToggleCollapse }) {
  const [dragOver, setDragOver] = useState(false);
  const [sortBy, setSortBy] = useState("registered");
  const [filterDay, setFilterDay] = useState("__all__");
  const [search, setSearch] = useState("");
  // Per-day collapse — a Set of ISO date strings that are currently hidden.
  // Powers the "busy day, one-click hide" feature in the sidebar.
  const [collapsedDays, setCollapsedDays] = useState(() => new Set());

  // Compute vacant slots by date once per render — powers the per-card
  // "Assign to vacant slot" dropdown AND the "Try auto-place" ranking.
  // Each slot carries the extra metadata (location, locked flag, start, end)
  // needed by the ranker to pick the smartest suggestion.
  const vacantByDay = useMemo(() => {
    const map = {};
    (days || []).forEach((d) => {
      const slots = [];
      (d.sessions || []).forEach((s) => {
        const lockCodes = s.role_locks || {};
        (s.roles || []).forEach((r) => {
          if (!s.cells || s.cells[r] == null) {
            slots.push({
              session: s.name,
              role: r,
              start: s.start || "",
              end: s.end || "",
              location: s.location || "",
              locked: !!lockCodes[r],
            });
          }
        });
      });
      map[d.date] = slots;
    });
    return map;
  }, [days]);

  const filtered = useMemo(() => {
    let arr = items || [];
    if (filterDay && filterDay !== "__all__") {
      arr = arr.filter((v) => (v.requested_day || "") === filterDay);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((v) =>
        (v.name || "").toLowerCase().includes(q) ||
        (v.email || "").toLowerCase().includes(q) ||
        (v.mobile || "").toLowerCase().includes(q)
      );
    }
    return arr;
  }, [items, filterDay, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sortBy === "name") {
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortBy === "day") {
      arr.sort((a, b) => (a.requested_day || "").localeCompare(b.requested_day || "")
        || (a.requested_session || "").localeCompare(b.requested_session || ""));
    } else {
      // registered = newest first (matches API order)
      arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return arr;
  }, [filtered, sortBy]);

  // Group visible cards by `requested_day` for the sidebar's per-day collapse
  // headers. Days without an ISO date fall into a "No preferred day" bucket
  // that admins can still hide.
  const grouped = useMemo(() => {
    const bucketMap = new Map();
    for (const v of sorted) {
      const key = v.requested_day || "__any__";
      if (!bucketMap.has(key)) bucketMap.set(key, []);
      bucketMap.get(key).push(v);
    }
    // Sort the buckets chronologically; the "any day" bucket sinks to the end.
    return Array.from(bucketMap.entries()).sort(([a], [b]) => {
      if (a === "__any__") return 1;
      if (b === "__any__") return -1;
      return a.localeCompare(b);
    });
  }, [sorted]);

  function toggleDay(dayKey) {
    setCollapsedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayKey)) next.delete(dayKey); else next.add(dayKey);
      return next;
    });
  }
  const allDayKeys = grouped.map(([k]) => k);
  const allCollapsed = allDayKeys.length > 0 && allDayKeys.every((k) => collapsedDays.has(k));
  function toggleAllDays() {
    setCollapsedDays(() => (allCollapsed ? new Set() : new Set(allDayKeys)));
  }

  const isSidebar = variant === "sidebar";
  const totalCount = (items || []).length;

  // Collapsed sidebar — a narrow vertical strip that still accepts drops so
  // admins can reclaim horizontal room but keep the "return to pool" gesture.
  if (isSidebar && collapsed) {
    return (
      <div
        data-testid="unallocated-sidebar-collapsed"
        onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); onDropHere?.(); }}
        className={`flex flex-col items-center border-2 border-dashed ${dragOver ? "border-[#002FA7] bg-[#002FA7]/[0.04]" : "border-black/20 bg-white"} p-2 w-12 min-h-[280px]`}
      >
        <button
          type="button"
          onClick={onToggleCollapse}
          data-testid="unallocated-expand-btn"
          title="Expand unallocated pool"
          className="mb-2 border border-black/10 bg-white px-1.5 py-1 text-[10px] hover:border-black"
        >
          ›
        </button>
        <div className="mt-2 text-[9px] font-black uppercase tracking-widest text-[#002FA7] [writing-mode:vertical-rl]">Unallocated</div>
        <div className="mt-2 rounded-full bg-[#002FA7] px-2 py-1 text-[10px] font-black text-white" data-testid="unallocated-collapsed-count">{totalCount}</div>
      </div>
    );
  }

  return (
    <div
      data-testid={isSidebar ? "unallocated-sidebar" : "unallocated-panel"}
      onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); onDropHere?.(); }}
      className={
        isSidebar
          ? `border-2 border-dashed ${dragOver ? "border-[#002FA7] bg-[#002FA7]/[0.04]" : "border-black/20 bg-white"} p-3`
          : `border-2 border-dashed p-4 min-h-[220px] ${dragOver ? "border-[#002FA7] bg-[#002FA7]/[0.04]" : "border-black/20 bg-white"}`
      }
    >
      <div className={isSidebar ? "mb-2" : "mb-3 flex items-baseline justify-between"}>
        <div className={isSidebar ? "flex items-start justify-between gap-2" : ""}>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#002FA7]">Unallocated pool</div>
            <h3 className={`mt-1 font-display font-black tracking-tighter ${isSidebar ? "text-base" : "text-xl"}`}>
              {isSidebar ? `${sorted.length} of ${totalCount} showing` : "Volunteers waiting for a role — pick a vacant slot below or drag them into any vacant cell."}
            </h3>
          </div>
          {isSidebar && onToggleCollapse ? (
            <button
              type="button"
              onClick={onToggleCollapse}
              data-testid="unallocated-collapse-btn"
              title="Collapse sidebar"
              className="shrink-0 border border-black/10 bg-white px-1.5 py-0.5 text-[10px] hover:border-black"
            >
              ‹
            </button>
          ) : null}
        </div>
        {!isSidebar ? (
          <span className="text-[11px] italic text-zinc-500">Drop a placed volunteer here to send them back to this pool (no email is sent).</span>
        ) : null}
      </div>
      {isSidebar ? (
        <div className="mb-2 grid grid-cols-1 gap-1.5">
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 w-10 shrink-0">Find</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email or mobile"
              data-testid="unallocated-search"
              className="flex-1 border border-black/20 bg-white px-2 py-1 text-[11px]"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                data-testid="unallocated-search-clear"
                title="Clear search"
                className="border border-black/10 bg-white px-1.5 text-[11px] hover:border-black"
              >×</button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 w-10 shrink-0">Sort</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="unallocated-sort"
              className="flex-1 border border-black/20 bg-white px-2 py-1 text-[11px]"
            >
              <option value="registered">Newest first</option>
              <option value="name">Name (A→Z)</option>
              <option value="day">Requested day</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 w-10 shrink-0">Day</label>
            <select
              value={filterDay}
              onChange={(e) => setFilterDay(e.target.value)}
              data-testid="unallocated-day-filter"
              className="flex-1 border border-black/20 bg-white px-2 py-1 text-[11px]"
            >
              <option value="__all__">All days</option>
              {(days || []).map((d) => (
                <option key={d.date} value={d.date}>{formatAuDateShort(d.date)}</option>
              ))}
            </select>
          </div>
        </div>
      ) : null}
      {isSidebar ? (
        <p className="mb-2 text-[10px] italic text-zinc-500">Drag a card into any spreadsheet cell — or drop a placed volunteer here to return them to the pool.</p>
      ) : null}
      {sorted.length === 0 ? (
        <p className="text-xs italic text-zinc-500">
          {(totalCount === 0)
            ? "No unallocated volunteers right now."
            : `No unallocated volunteers match this day filter.`}
        </p>
      ) : isSidebar ? (
        <div data-testid="unallocated-list" className="flex flex-col gap-2">
          {grouped.length > 1 ? (
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={toggleAllDays}
                data-testid="unallocated-toggle-all-days"
                className="text-[10px] uppercase tracking-widest text-[#002FA7] underline underline-offset-2 hover:text-black"
              >
                {allCollapsed ? "Expand all days" : "Collapse all days"}
              </button>
            </div>
          ) : null}
          {grouped.map(([dayKey, rows]) => {
            const isHidden = collapsedDays.has(dayKey);
            const label = dayKey === "__any__" ? "No preferred day" : formatAuDateShort(dayKey);
            return (
              <div key={dayKey} className="border border-black/10" data-testid={`unallocated-day-group-${dayKey}`}>
                <button
                  type="button"
                  onClick={() => toggleDay(dayKey)}
                  data-testid={`unallocated-day-toggle-${dayKey}`}
                  className="flex w-full items-center justify-between gap-2 bg-zinc-50 px-2 py-1 text-left hover:bg-[#002FA7]/[0.05]"
                  aria-expanded={!isHidden}
                >
                  <span className="flex items-center gap-1.5">
                    <span className={`inline-block text-[10px] transition-transform ${isHidden ? "-rotate-90" : ""}`} aria-hidden="true">▾</span>
                    <span className="text-[11px] font-black uppercase tracking-widest text-[#002FA7]">{label}</span>
                  </span>
                  <span className="rounded-full bg-[#002FA7] px-1.5 py-0.5 text-[9px] font-black text-white" data-testid={`unallocated-day-count-${dayKey}`}>
                    {rows.length}
                  </span>
                </button>
                {!isHidden ? (
                  <ul className="flex flex-col gap-2 p-2" data-testid={`unallocated-day-list-${dayKey}`}>
                    {rows.map((v) => (
                      <UnallocatedCard
                        key={v.volunteer_id}
                        v={v}
                        vacantSlots={vacantByDay[v.requested_day] || []}
                        vacantByDay={vacantByDay}
                        days={days || []}
                        eventId={eventId}
                        onAssigned={onAssigned}
                        compact={true}
                      />
                    ))}
                  </ul>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <ul
          className="grid gap-2 md:grid-cols-2 lg:grid-cols-3"
          data-testid="unallocated-list"
        >
          {sorted.map((v) => (
            <UnallocatedCard
              key={v.volunteer_id}
              v={v}
              vacantSlots={vacantByDay[v.requested_day] || []}
              vacantByDay={vacantByDay}
              days={days || []}
              eventId={eventId}
              onAssigned={onAssigned}
              compact={false}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function UnallocatedCard({ v, vacantSlots, vacantByDay, days, eventId, onAssigned, compact }) {
  // `pick` is now day-scoped so auto-place can suggest a slot on a different
  // day than the volunteer originally requested. Falls back to the requested
  // day when the admin picks from the plain dropdown.
  const [pick, setPick] = useState("");
  const [suggestion, setSuggestion] = useState(null); // { day, session, role, reason }
  const [busy, setBusy] = useState(false);

  // Rank vacant slots according to the user-specified priority:
  //   1. Exact original: same day + same session + same role (not locked)
  //   2. Same day + same session (any role, not locked)
  //   3. Same day + same time window (not locked)
  //   4. Same day + same location (not locked)
  //   5. Same day + any not-locked role
  //   6. Fallback: next available day, any not-locked role
  function computeSuggestion() {
    const reqDay = v.requested_day || "";
    const reqSess = v.requested_session || "";
    const reqRole = v.requested_role || "";
    const reqStart = v.requested_start || "";
    const reqEnd = v.requested_end || "";
    const reqLocRaw = (vacantSlots.find((s) => s.session === reqSess)?.location || "").trim().toLowerCase();

    const sameDay = (vacantByDay[reqDay] || []).filter((s) => !s.locked);
    // 1
    const exact = sameDay.find((s) => s.session === reqSess && s.role === reqRole);
    if (exact) return { day: reqDay, ...exact, reason: "Exact match: same day, session and role" };
    // 2
    const sameSess = sameDay.find((s) => s.session === reqSess);
    if (sameSess) return { day: reqDay, ...sameSess, reason: `Same session — different role (${sameSess.role})` };
    // 3
    if (reqStart || reqEnd) {
      const sameTime = sameDay.find((s) => (s.start || "") === reqStart && (s.end || "") === reqEnd);
      if (sameTime) return { day: reqDay, ...sameTime, reason: "Same day, same time window" };
    }
    // 4 — location match (nearest = case-insensitive string equality; we have
    // no lat/lng so equality of the venue name is the best proxy).
    if (reqLocRaw) {
      const sameLoc = sameDay.find((s) => (s.location || "").trim().toLowerCase() === reqLocRaw);
      if (sameLoc) return { day: reqDay, ...sameLoc, reason: `Same day at ${sameLoc.location}` };
    }
    // 5
    if (sameDay[0]) return { day: reqDay, ...sameDay[0], reason: "Same day, next available not-locked role" };
    // 6 — walk other days in chronological order
    const otherDays = (days || []).map((d) => d.date).filter((d) => d && d !== reqDay).sort();
    for (const d of otherDays) {
      const slot = (vacantByDay[d] || []).find((s) => !s.locked);
      if (slot) return { day: d, ...slot, reason: `Alternate day — ${formatAuDateShort(d)}` };
    }
    return null;
  }

  function handleAutoPlace() {
    const s = computeSuggestion();
    if (!s) {
      toast.error("No unlocked vacant slots left on any day.");
      setSuggestion(null);
      return;
    }
    setSuggestion(s);
    // Pre-populate the dropdown with the suggested day+session+role so the
    // admin can eyeball it before clicking Assign.
    setPick(`${s.day}||${s.session}||${s.role}`);
  }

  async function doAssign(day, session, role) {
    const slot = (vacantByDay[day] || []).find((s) => s.session === session && s.role === role);
    const timeStr = slot && (slot.start || slot.end) ? ` (${slot.start || "?"}–${slot.end || "?"})` : "";
    const proceed = window.confirm(
      `Assign ${v.name} to:\n\n` +
      `  ${day} · ${session}${timeStr} · ${role}\n\n` +
      `A confirmation email will be sent to ${v.email} asking them to accept the role.\n\n` +
      `Click OK to assign, or Cancel to keep them unallocated.`
    );
    if (!proceed) return;
    setBusy(true);
    try {
      await api.post(`/admin/events/${eventId}/volunteers/${v.volunteer_id}/reassign`, { day, session, role });
      toast.success(`Assigned ${v.name} → ${session} · ${role}. Confirmation email sent.`);
      onAssigned?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assign failed");
    } finally {
      setBusy(false);
      setSuggestion(null);
    }
  }

  async function handleAssign() {
    if (!pick) { toast.error("Pick a vacant slot first."); return; }
    // pick format = "YYYY-MM-DD||Session||Role" — includes day so admins can
    // assign this person to any vacancy on any day (issue #2 feedback).
    const [day, session, role] = pick.split("||");
    await doAssign(day, session, role);
  }

  // Total vacant slot count across every day — drives the "no slots at all"
  // empty state.
  const totalVacant = Object.values(vacantByDay || {}).reduce((n, arr) => n + (arr || []).length, 0);

  const timeStr = (v.requested_start || v.requested_end)
    ? `${v.requested_start || "?"}–${v.requested_end || "?"}`
    : "";

  return (
    <li
      draggable
      onDragStart={() => { window.__volmee_drag = { vid: v.volunteer_id, name: v.name, day: v.requested_day, session: "(unallocated)", role: "(unallocated)" }; }}
      onDragEnd={() => { window.__volmee_drag = null; }}
      data-testid={`unallocated-card-${v.volunteer_id}`}
      className={`cursor-grab border ${v.has_time_conflict && !v.conflict_approved ? "border-[#FF3B30] ring-2 ring-[#FF3B30]/40" : "border-black/15"} bg-white ${compact ? "p-2" : "p-3"} shadow-[3px_3px_0_0_rgba(0,47,167,0.08)] hover:shadow-[5px_5px_0_0_rgba(0,47,167,0.15)]`}
    >
      <div className={`font-semibold ${compact ? "text-[12px]" : "text-sm"} flex items-center gap-1.5`}>
        {v.has_time_conflict && !v.conflict_approved ? (
          <span data-testid={`unallocated-conflict-flag-${v.volunteer_id}`} title="Double-booked on the same day" className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#FF3B30] text-white shrink-0">
            <span className="text-[8px] font-black">!</span>
          </span>
        ) : null}
        {v.has_time_conflict && v.conflict_approved ? (
          <span data-testid={`unallocated-conflict-approved-${v.volunteer_id}`} title="Conflict approved by admin" className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-white shrink-0">
            <span className="text-[9px] font-black">✓</span>
          </span>
        ) : null}
        <span className="truncate">{v.name}</span>
      </div>
      {!compact ? (
        <>
          <div className="mt-0.5 font-mono text-[11px] text-zinc-600">{v.email}</div>
          {v.mobile ? <div className="font-mono text-[11px] text-zinc-600">{v.mobile}</div> : null}
        </>
      ) : null}

      {/* Requested slot summary — what they signed up for. */}
      <div className={`mt-1 ${compact ? "space-y-0.5" : ""} text-[10px] text-[#002FA7]`}>
        <div>
          <span className="uppercase tracking-widest">Day:</span>{" "}
          <span className="font-semibold">{v.requested_day ? formatAuDateShort(v.requested_day) : "any day"}</span>
        </div>
        {v.requested_session ? (
          <div>
            <span className="uppercase tracking-widest">Session:</span>{" "}
            <span className="font-semibold">{v.requested_session}</span>
            {timeStr ? <span className="ml-1 font-mono text-[10px] text-zinc-500">({timeStr})</span> : null}
          </div>
        ) : null}
        {v.requested_role ? (
          <div>
            <span className="uppercase tracking-widest">Role:</span>{" "}
            <span className="font-semibold">{v.requested_role}</span>
          </div>
        ) : null}
        {(v.preferences || []).length > 0 ? (
          <div className="mt-0.5 italic text-[9px] text-zinc-500">Prefs: {v.preferences.join(", ")}</div>
        ) : null}
      </div>

      {v.has_time_conflict && !v.conflict_approved ? (
        <div className="mt-1.5 border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-1.5" data-testid={`unallocated-conflict-banner-${v.volunteer_id}`}>
          <div className="text-[9px] font-black uppercase tracking-widest text-[#FF3B30]">Time conflict</div>
          <p className="mt-0.5 text-[10px] text-zinc-700">This person has two overlapping slots on the same day.</p>
          <button
            type="button"
            onClick={async (e) => {
              e.stopPropagation();
              if (!window.confirm(`Approve ${v.name} being in two overlapping roles?\n\nThis marks the conflict as reviewed by you. The red flag will disappear.`)) return;
              try {
                await api.post(`/admin/events/${eventId}/volunteers/${v.volunteer_id}/approve-conflict`);
                toast.success("Conflict approved.");
                onAssigned?.();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Approve failed");
              }
            }}
            data-testid={`unallocated-approve-conflict-${v.volunteer_id}`}
            className="mt-1 w-full border border-emerald-600 bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-white hover:text-emerald-700"
          >Approve conflict</button>
        </div>
      ) : null}

      <div className={`${compact ? "mt-1.5" : "mt-2"} border-t border-black/10 pt-1.5`} onDragStart={(e) => e.stopPropagation()}>
        {/* Try auto-place — surfaces the ranked best-fit vacancy so the admin
            can review it before firing the confirmation email. */}
        <button
          type="button"
          onClick={handleAutoPlace}
          disabled={busy}
          data-testid={`auto-place-btn-${v.volunteer_id}`}
          className="mb-1.5 inline-flex w-full items-center justify-center gap-1 border border-[#002FA7] bg-[#002FA7]/[0.06] px-2 py-1 text-[10px] font-semibold text-[#002FA7] hover:bg-[#002FA7] hover:text-white disabled:opacity-40"
          title="Suggest the best-fit vacant slot based on their original request"
        >
          ⚡ Try auto-place
        </button>
        {suggestion ? (
          <div className="mb-1.5 border border-[#002FA7]/40 bg-[#002FA7]/[0.04] p-1.5" data-testid={`auto-place-suggestion-${v.volunteer_id}`}>
            <div className="text-[9px] font-black uppercase tracking-widest text-[#002FA7]">Suggested</div>
            <div className="mt-0.5 text-[10px] font-semibold">
              {formatAuDateShort(suggestion.day)} · {suggestion.session}
              {(suggestion.start || suggestion.end) ? <span className="ml-1 font-mono text-[10px] text-zinc-500">({suggestion.start || "?"}–{suggestion.end || "?"})</span> : null}
              {suggestion.location ? <span className="ml-1 text-[10px] text-zinc-600">· 📍 {suggestion.location}</span> : null}
              <span className="ml-1">· {suggestion.role}</span>
            </div>
            <div className="mt-0.5 text-[9px] italic text-zinc-600" data-testid={`auto-place-reason-${v.volunteer_id}`}>{suggestion.reason}</div>
            <div className="mt-1 flex gap-1">
              <button
                type="button"
                onClick={() => doAssign(suggestion.day, suggestion.session, suggestion.role)}
                disabled={busy}
                data-testid={`auto-place-accept-${v.volunteer_id}`}
                className="flex-1 border border-black bg-black px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-white hover:text-black disabled:opacity-40"
              >
                {busy ? "…" : "Assign this"}
              </button>
              <button
                type="button"
                onClick={() => setSuggestion(null)}
                data-testid={`auto-place-dismiss-${v.volunteer_id}`}
                className="border border-black/20 bg-white px-2 py-0.5 text-[10px] hover:border-black"
              >Dismiss</button>
            </div>
          </div>
        ) : null}
        {totalVacant === 0 ? (
          <p className="text-[10px] italic text-zinc-500" data-testid={`assign-no-slots-${v.volunteer_id}`}>
            No vacant slots left on any day.
          </p>
        ) : (
          <>
            <label className="mb-1 block text-[9px] uppercase tracking-widest text-zinc-500">Assign to vacant slot (any day)</label>
            <div className="flex items-center gap-1">
              <select
                value={pick}
                onChange={(e) => setPick(e.target.value)}
                data-testid={`assign-select-${v.volunteer_id}`}
                className="flex-1 min-w-0 border border-black/20 bg-white px-2 py-1 text-[11px]"
              >
                <option value="">Pick a day + slot…</option>
                {(days || []).map((d) => {
                  const slots = vacantByDay[d.date] || [];
                  if (slots.length === 0) return null;
                  return (
                    <optgroup key={d.date} label={formatAuDateShort(d.date)}>
                      {slots.map((s, i) => (
                        <option key={`${d.date}||${s.session}||${s.role}||${i}`} value={`${d.date}||${s.session}||${s.role}`}>
                          {s.locked ? "🔒 " : ""}{s.session}{(s.start || s.end) ? ` (${s.start || "?"}–${s.end || "?"})` : ""}{s.location ? ` · 📍 ${s.location}` : ""} · {s.role}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
              <button
                type="button"
                onClick={handleAssign}
                disabled={busy || !pick}
                data-testid={`assign-btn-${v.volunteer_id}`}
                className="shrink-0 border border-black bg-black px-2 py-1 text-[10px] font-semibold text-white hover:bg-white hover:text-black disabled:opacity-40"
              >
                {busy ? "…" : "Assign"}
              </button>
            </div>
          </>
        )}
      </div>
    </li>
  );
}
